import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { useIntl } from 'react-intl';
import { gql } from '@apollo/client';
import { withSnackbar } from 'notistack';
import { formatDate } from '../../../../utils/formatters';
import KnockoutQuestionsSection from './KnockoutQuestionsSection';
import ScheduledMeetingsSection from './ScheduledMeetingsSection';
import ProposalLetterSection from './ProposalLetterSection';
import RecommendationSectionDisplay from './RecommendationSectionDisplay';
import FileTab from '../../../../containers/FileTabContainer';
import ChatWidget from '../../../../containers/ChatWidgetContainer';

// GraphQL Mutations
const INVITE_MUTATION = gql`
  mutation inviteUser($deal_interest: String!) {
    inviteUser(deal_interest: $deal_interest)
  }
`;

const SHORTLIST_DEAL_INTEREST = gql`
  mutation shortlistDealInterest($dealId: ID!, $dealInterestId: ID!) {
    shortlistDealInterest(dealId: $dealId, dealInterestId: $dealInterestId) {
      dealInterest {
        id
        workflow_state
        shortlisted
      }
      errors
    }
  }
`;

const ARCHIVE_DEAL_INTEREST = gql`
  mutation archiveDealInterest($dealId: ID!, $dealInterestId: ID!, $declined: Boolean!) {
    archiveDealInterest(dealId: $dealId, dealInterestId: $dealInterestId, declined: $declined) {
      dealInterest {
        id
        workflow_state
      }
      errors
    }
  }
`;

const SEND_NDA_TO_RECEIVING_PARTY = gql`
  mutation sendNdaToReceivingParty($dealId: ID!, $dealInterestId: ID!) {
    sendNdaToReceivingParty(dealId: $dealId, dealInterestId: $dealInterestId) {
      dealInterest {
        id
        workflow_state
        receiving_party_nda_sent_timestamp
      }
      errors
    }
  }
`;

const DealSection = ({ dealInterest, apolloClient, enqueueSnackbar, onClose, company, currentUser }) => {
  const intl = useIntl();
  // Separate loading states for each action
  const [isInviting, setIsInviting] = useState(false);
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [isUnshortlisting, setIsUnshortlisting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isSendingNDA, setIsSendingNDA] = useState(false);
  
  // Combined loading state for disabling all buttons
  const isLoading = isInviting || isShortlisting || isUnshortlisting || isArchiving || isSendingNDA;
  
  const [shortlistStatus, setShortlistStatus] = useState(dealInterest?.shortlisted || false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [localWorkflowState, setLocalWorkflowState] = useState(get(dealInterest, 'workflow_state'));

  const workflowState = localWorkflowState;
  const deal = get(dealInterest, 'deal', {});
  const dealId = get(deal, 'id');
  const dealInterestId = get(dealInterest, 'id');
  const dealName = get(deal, 'deal_name');
  
  // Deal room messaging settings
  const allowShortlistMessaging = get(company, 'deal_room_allow_messaging_when_shortlisted', false);
  const isPastShortlisted = ['out_for_receiving_signature', 'awaiting_disclosing_signature', 'nda_completed', 'negotiating'].includes(workflowState);
  const showDealMessaging = (workflowState === 'shortlisted' && allowShortlistMessaging) || isPastShortlisted;

  // Helper function to show success animation
  const showSuccessAnimation = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Invite to deal
  const handleInvite = async () => {
    try {
      setIsInviting(true);
      await apolloClient.mutate({
        mutation: INVITE_MUTATION,
        variables: { deal_interest: dealInterestId }
      });
      
      showSuccessAnimation(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_invitation_sent' }));
      enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_invitation_sent' }), { variant: 'success' });
      
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('close-succession-drawer'));
      }, 1500);
    } catch (error) {
      console.error('Error inviting user:', error);
      enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_invitation_failed' }), { variant: 'error' });
    } finally {
      setIsInviting(false);
    }
  };

  // Shortlist deal interest
  const handleShortlist = async () => {
    try {
      setIsShortlisting(true);
      const response = await apolloClient.mutate({
        mutation: SHORTLIST_DEAL_INTEREST,
        variables: { dealId, dealInterestId }
      });

      if (response.data?.shortlistDealInterest?.dealInterest) {
        const updatedDealInterest = response.data.shortlistDealInterest.dealInterest;
        setShortlistStatus(true);
        setLocalWorkflowState(updatedDealInterest.workflow_state);
        showSuccessAnimation(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_shortlisted' }));
        enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_shortlisted' }), { variant: 'success' });
        
        // Dispatch navigation event
        window.dispatchEvent(new CustomEvent('advisor-shortlisted-navigate', {
          detail: {
            dealName,
            targetUrl: `/succession/deals/${dealName}/prospects/shortlisted`,
            shouldRefresh: true
          }
        }));
        
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('close-succession-drawer'));
        }, 1500);
      } else if (response.data?.shortlistDealInterest?.errors?.length) {
        enqueueSnackbar(response.data.shortlistDealInterest.errors.join(', '), { variant: 'error' });
      }
    } catch (error) {
      console.error('Error shortlisting:', error);
      enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_shortlist_failed' }), { variant: 'error' });
    } finally {
      setIsShortlisting(false);
    }
  };

  // Unshortlist
  const handleUnshortlist = async () => {
    try {
      setIsUnshortlisting(true);
      const response = await apolloClient.mutate({
        mutation: SHORTLIST_DEAL_INTEREST,
        variables: { dealId, dealInterestId }
      });

      if (response.data?.shortlistDealInterest?.dealInterest) {
        const updatedDealInterest = response.data.shortlistDealInterest.dealInterest;
        setShortlistStatus(false);
        setLocalWorkflowState(updatedDealInterest.workflow_state);
        enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_removed_from_shortlist' }), { variant: 'success' });
      }
    } catch (error) {
      console.error('Error unshortlisting:', error);
      enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_remove_shortlist_failed' }), { variant: 'error' });
    } finally {
      setIsUnshortlisting(false);
    }
  };

  // Archive deal interest
  const handleArchive = async (declined = false) => {
    try {
      setIsArchiving(true);
      const response = await apolloClient.mutate({
        mutation: ARCHIVE_DEAL_INTEREST,
        variables: { dealId, dealInterestId, declined }
      });

      if (response.data?.archiveDealInterest?.dealInterest) {
        const updatedDealInterest = response.data.archiveDealInterest.dealInterest;
        setLocalWorkflowState(updatedDealInterest.workflow_state);
        enqueueSnackbar(intl.formatMessage({ id: declined ? 'succession.profile_drawer.deal_section.toast_advisor_declined' : 'succession.profile_drawer.deal_section.toast_advisor_archived' }), { variant: 'success' });
        
        setTimeout(() => {
          window.location.href = `/succession/deals/${dealName}/prospects/matches`;
        }, 1000);
      } else if (response.data?.archiveDealInterest?.errors?.length) {
        enqueueSnackbar(response.data.archiveDealInterest.errors.join(', '), { variant: 'error' });
      }
    } catch (error) {
      console.error('Error archiving:', error);
      enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_archive_failed' }), { variant: 'error' });
    } finally {
      setIsArchiving(false);
    }
  };

  // Send NDA
  const handleSendNDA = async () => {
    try {
      setIsSendingNDA(true);
      const response = await apolloClient.mutate({
        mutation: SEND_NDA_TO_RECEIVING_PARTY,
        variables: { dealId, dealInterestId }
      });

      if (response.data?.sendNdaToReceivingParty?.dealInterest) {
        const updatedDealInterest = response.data.sendNdaToReceivingParty.dealInterest;
        setLocalWorkflowState(updatedDealInterest.workflow_state);
        showSuccessAnimation(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_nda_sent' }));
        enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_nda_sent' }), { variant: 'success' });
        
        // Dispatch navigation event
        window.dispatchEvent(new CustomEvent('advisor-nda-sent-navigate', {
          detail: {
            dealName,
            targetUrl: `/succession/deals/${dealName}/prospects/ndas`,
            shouldRefresh: true
          }
        }));
        
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('close-succession-drawer'));
        }, 1500);
      } else if (response.data?.sendNdaToReceivingParty?.errors?.length) {
        enqueueSnackbar(response.data.sendNdaToReceivingParty.errors.join(', '), { variant: 'error' });
      }
    } catch (error) {
      console.error('Error sending NDA:', error);
      enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_nda_failed' }), { variant: 'error' });
    } finally {
      setIsSendingNDA(false);
    }
  };

  // Sign NDA
  const handleSignNDA = () => {
    const advisorId = get(dealInterest, 'inventory_user.user.id');
    const matchId = dealInterestId;
    const returnUrl = `${encodeURIComponent(window.location.href.split('?')[0])}/return/${matchId}`;
    window.location.href = `/deal/nda_sign_discloser?deal_id=${dealId}&match_id=${matchId}&advisor_id=${advisorId}&return_url=${returnUrl}`;
  };

  // Download NDA
  const handleDownloadNDA = () => {
    window.open(`/deal/${dealName}/open_file/${dealInterestId}`, '_blank');
  };

  // Get workflow state display name
  const getWorkflowStateDisplay = () => {
    const stateMap = {
      'matching': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_matching' }),
      'invited': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_invited' }),
      'reviewing_proposal': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_reviewing_proposal' }),
      'shortlisted': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_shortlisted' }),
      'out_for_receiving_signature': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_nda_out_for_signature' }),
      'awaiting_disclosing_signature': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_awaiting_your_signature' }),
      'nda_completed': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_nda_completed' }),
      'negotiating': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_negotiating' }),
      'declined': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_declined' }),
      'archived': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_archived' }),
    };
    return stateMap[workflowState] || workflowState;
  };

  // Get badge color
  const getBadgeColor = () => {
    const colorMap = {
      'matching': 'bg-green-500',
      'invited': 'bg-blue-500',
      'reviewing_proposal': 'bg-yellow-500',
      'shortlisted': 'bg-purple-500',
      'out_for_receiving_signature': 'bg-orange-600',
      'awaiting_disclosing_signature': 'bg-red-500',
      'nda_completed': 'bg-teal-500',
      'negotiating': 'bg-indigo-500',
      'declined': 'bg-gray-500',
      'archived': 'bg-amber-700'
    };
    return colorMap[workflowState] || 'bg-gray-500';
  };

  // Render action buttons based on workflow state
  const renderActionButtons = () => {
    switch (workflowState) {
      case 'matching':
        return (
          <button
            onClick={handleInvite}
            disabled={isLoading}
            className="w-full px-6 py-3 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isInviting ? intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sending' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.invite_to_deal' })}
          </button>
        );

      case 'invited':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-blue-700 font-medium">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.invitation_sent' })}</p>
            <p className="text-sm text-blue-600 mt-1">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.waiting_for_response' })}</p>
          </div>
        );

      case 'reviewing_proposal':
        return (
          <div className="space-y-3">
            <button
              onClick={handleShortlist}
              disabled={isLoading}
              className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isShortlisting ? intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sending' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.shortlist_advisor' })}
            </button>
            <button
              onClick={() => handleArchive(false)}
              disabled={isLoading}
              className="w-full px-6 py-3 border border-succession-border text-succession-text-primary rounded-lg hover:bg-succession-bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isArchiving ? intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sending' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.archive' })}
            </button>
          </div>
        );

      case 'shortlisted':
        return (
          <div className="space-y-3">
            <button
              onClick={handleSendNDA}
              disabled={isLoading}
              className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isSendingNDA ? intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sending' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.send_nda' })}
            </button>
            <button
              onClick={handleUnshortlist}
              disabled={isLoading}
              className="w-full px-6 py-3 border border-succession-border text-succession-text-primary rounded-lg hover:bg-succession-bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUnshortlisting ? intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sending' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.remove_from_shortlist' })}
            </button>
            <button
              onClick={() => handleArchive(true)}
              disabled={isLoading}
              className="w-full px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isArchiving ? intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sending' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.decline' })}
            </button>
          </div>
        );

      case 'out_for_receiving_signature':
        return (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
            <p className="text-orange-700 font-medium">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_nda_out_for_signature' })}</p>
            <p className="text-sm text-orange-600 mt-1">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.nda_waiting_for_sign' })}</p>
            {dealInterest.receiving_party_nda_sent_timestamp && (
              <p className="text-xs text-orange-600 mt-2">
                {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sent_label' })} {formatDate(dealInterest.receiving_party_nda_sent_timestamp)}
              </p>
            )}
          </div>
        );

      case 'awaiting_disclosing_signature':
        return (
          <button
            onClick={handleSignNDA}
            className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
          >
            {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sign_nda' })}
          </button>
        );

      case 'nda_completed':
      case 'negotiating':
        return (
          <div className="space-y-3">
            <button
              onClick={handleDownloadNDA}
              className="w-full px-6 py-3 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
            >
              {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.download_nda' })}
            </button>
            {workflowState === 'negotiating' && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
                <p className="text-indigo-700 font-medium">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.negotiation_phase' })}</p>
                <p className="text-sm text-indigo-600 mt-1">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.use_messaging' })}</p>
              </div>
            )}
          </div>
        );

      case 'archived':
      case 'declined':
        return (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-gray-700 font-medium">{workflowState === 'declined' ? intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_declined' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_archived' })}</p>
            <p className="text-sm text-gray-600 mt-1">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.opportunity_inactive' })}</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      {/* Success Animation */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-8 shadow-2xl max-w-md mx-4 animate-bounce">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xl font-semibold text-gray-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header with Status Badge */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.deal_information' })}</h3>
        <span className={`px-3 py-1 ${getBadgeColor()} text-white text-sm font-medium rounded-full`}>
          {getWorkflowStateDisplay()}
        </span>
      </div>


      {/* Recommendation Section */}
      {dealInterest.reason_for_suggestion && (
        <div className="mb-6">
          <RecommendationSectionDisplay reasonForSuggestion={dealInterest.reason_for_suggestion} />
        </div>
      )}

      {/* Proposal Letter */}
      {dealInterest.proposal_letter && workflowState !== 'matching' && (
        <div className="mb-6">
          <ProposalLetterSection 
            proposalLetter={dealInterest.proposal_letter}
            highlight={workflowState === 'reviewing_proposal'}
          />
        </div>
      )}

      {/* Knockout Questions */}
      {dealInterest.a1 && (
        <div className="mb-6">
          <KnockoutQuestionsSection dealInterest={dealInterest} />
        </div>
      )}

      {/* Scheduled Meetings */}
      {!isEmpty(get(dealInterest, 'deal_scheduled_meetings', [])) && (
        <div className="mb-6">
          <ScheduledMeetingsSection meetings={dealInterest.deal_scheduled_meetings} />
        </div>
      )}

      {/* File Sharing - Only in negotiating state */}
      {workflowState === 'negotiating' && (
        <div className="mb-6">
          <div className="bg-succession-bg-secondary rounded-lg border border-succession-border overflow-hidden">
            <div className="px-6 py-4 border-b border-succession-border">
              <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
                {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.document_sharing' })}
              </h3>
              <p className="text-sm text-succession-text-muted">
                {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.document_sharing_subtitle' })}
              </p>
            </div>
            <div className="px-6 py-6">
              <FileTab
                variables={{ deal_interest_id: dealInterestId }}
                documents={dealInterest.documents || []}
                disablePopup={true}
                disableUpload={get(deal, 'closed', false)}
                dropMessage="Drag and drop files here or click to upload documents for this negotiation"
              />
            </div>
          </div>
        </div>
      )}

      {/* Deal-Specific Messaging - Shows for shortlisted+ */}
      {showDealMessaging && dealInterest.conversation_id && (
        <div className="mb-6">
          <div className="bg-succession-bg-secondary rounded-lg border border-succession-border overflow-hidden">
            <div className="px-6 py-4 border-b border-succession-border">
              <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
                {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.deal_room_messages' })}
              </h3>
              <p className="text-sm text-succession-text-muted">
                {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.deal_room_messages_subtitle' })}
              </p>
            </div>
            <div className="px-6 py-6">
              <ChatWidget
                conversationId={dealInterest.conversation_id}
                showChatBox={true}
                startConvo={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6">
        {renderActionButtons()}
      </div>
    </div>
  );
};

DealSection.propTypes = {
  dealInterest: PropTypes.object.isRequired,
  apolloClient: PropTypes.object.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  company: PropTypes.object,
  currentUser: PropTypes.object,
};

export default withSnackbar(DealSection);

